import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TikTokIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={props.strokeWidth || 1.5} strokeLinecap="round" strokeLinejoin="round" className={props.className}>
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const ThreadsIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={props.strokeWidth || 1.5} strokeLinecap="round" strokeLinejoin="round" className={props.className}>
    <path d="M19 7.5c-1.333-3-3.667-4.5-7-4.5-5 0-8 2.5-8 9s3.5 9 8 9 7-3 7-5-1-5-7-5c-2.5 0-3 1.25-3 2.5 0 1.5 1 2.5 2.5 2.5 2.5 0 3.5-1.5 3.5-5s-2-4-3-4-1.833.333-2.5 1" />
  </svg>
);

const SOCIALS = [
  { label: "Instagram", href: "https://www.instagram.com/iambenzthebrand", Icon: Instagram },
  { label: "TikTok", href: "https://www.tiktok.com/@iambenzthebrand", Icon: TikTokIcon },
  { label: "Threads", href: "https://www.threads.com/@iambenzthebrand", Icon: ThreadsIcon },
];

const Footer = () => {
  const [email, setEmail] = useState("");
  const [joining, setJoining] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setJoining(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list, welcome to the inner circle.");
      setEmail("");
    } catch {
      toast.error("Couldn't sign you up right now. Please try again.");
    } finally {
      setJoining(false);
    }
  };

  return (
    <footer className="bg-[#4A3F35] text-[#FAF7F2] mt-24" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-2 lg:grid-cols-4 gap-14">
        <div>
          <h3 className="font-serif-display text-3xl mb-4 leading-tight">
            Benz<span className="text-[#C6A769]">the</span>Brand
          </h3>
          <p className="text-sm text-[#E8CFCB]/80 max-w-xs leading-relaxed">
            Luxury Is the Lifestyle. Wealth Is the Goal. Digital products to help
            you build the life you keep imagining.
          </p>
          <div className="flex gap-5 mt-7">
            {SOCIALS.map(({ label, href, Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`footer-social-${label.toLowerCase()}`}
                className="text-[#E8CFCB] hover:text-[#C6A769] transition-colors duration-300"
              >
                <Icon className="w-5 h-5" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C6A769] mb-5">Explore</p>
          <ul className="space-y-3 text-sm text-[#FAF7F2]/85">
            <li><Link to="/" className="hover:text-[#C6A769] transition-colors">Home</Link></li>
            <li><Link to="/shop" className="hover:text-[#C6A769] transition-colors">Shop All</Link></li>
            <li><Link to="/cart" className="hover:text-[#C6A769] transition-colors">Cart</Link></li>
            <li><Link to="/contact" data-testid="footer-link-contact" className="hover:text-[#C6A769] transition-colors">Contact Us</Link></li>
            <li><Link to="/resend" data-testid="footer-link-resend" className="hover:text-[#C6A769] transition-colors">Resend My Downloads</Link></li>
          </ul>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C6A769] mb-5">Legal</p>
          <ul className="space-y-3 text-sm text-[#FAF7F2]/85">
            <li><Link to="/terms" data-testid="footer-link-terms" className="hover:text-[#C6A769] transition-colors">Terms of Use</Link></li>
            <li><Link to="/refund" data-testid="footer-link-refund" className="hover:text-[#C6A769] transition-colors">Refund &amp; Chargeback Policy</Link></li>
            <li><Link to="/disclaimer" data-testid="footer-link-disclaimer" className="hover:text-[#C6A769] transition-colors">Disclaimer</Link></li>
            <li><Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-[#C6A769] transition-colors">Privacy Policy</Link></li>
          </ul>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C6A769] mb-5">The Newsletter</p>
          <p className="text-sm text-[#FAF7F2]/80 mb-5 leading-relaxed">
            Get new drops, wealth notes & exclusive offers in your inbox.
          </p>
          <form onSubmit={subscribe} className="flex border-b border-[#FAF7F2]/30 focus-within:border-[#C6A769] transition-colors">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="newsletter-email-input"
              className="bg-transparent flex-1 py-2 text-sm outline-none placeholder:text-[#FAF7F2]/40"
            />
            <button
              type="submit"
              disabled={joining}
              data-testid="newsletter-submit"
              className="text-xs uppercase tracking-[0.2em] text-[#C6A769] pl-4 disabled:opacity-60"
            >
              {joining ? "…" : "Join"}
            </button>
          </form>
        </div>
      </div>
      <div className="border-t border-[#FAF7F2]/10 py-6 text-center text-xs text-[#FAF7F2]/50">
        © {new Date().getFullYear()} BenztheBrand. All rights reserved.
      </div>
    </footer>
  );
};

export default Footer;
